import {
  StatisticType,
  AdgroupConfig,
  AdgroupFormData,
  AppEvents,
  CampaignFormData,
  DeepBidType,
  EMEventType
} from '@tt4b/ttam-enums';
import { useAdgroupAppEvents, useStdObjectiveType } from '@tt4b/ttam-hooks';
import { Ref, computed } from '@vue/composition-api';
import { i18n } from './../../../../i18n';
import { useCurrencyOption } from './../../../../hooks/creation/account/useCurrencyOption/index';

function useCommonSecondaryGoal(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  appEvents: Ref<AppEvents>;
}) {
  const { appEvents, campaignData, adgroupData } = props;
  const { curDeepExternalActionItem } = useAdgroupAppEvents({
    appEvents,
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  return computed(() => i18n.t(curDeepExternalActionItem.value?.external_action_i18nkey || ''));
}

function useCommonDeepCurrencyUnit(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  appEvents: Ref<AppEvents>;
}) {
  const { appEvents, campaignData, adgroupData } = props;
  const { curDeepExternalActionItem } = useAdgroupAppEvents({
    appEvents,
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  const { isAppInstall } = useStdObjectiveType({
    campaignFormData: campaignData,
  });

  const { currencyOption } = useCurrencyOption();

  /** 深度出价单位 */
  const deepBidUnit = computed(() => {
    const type = i18n.t(curDeepExternalActionItem.value?.external_action_i18nkey || '');

    if (!type || !isAppInstall.value) {
      return type;
    }

    return adgroupData.value?.statisticType === StatisticType.EACH_EVENT ?
      i18n.t('module_bid_unit_each', { type }) :
      i18n.t('module_bid_unit_unique', { type });
  });

  return computed(() => `${currencyOption.value?.currency}/${deepBidUnit.value}`);
}

/** DeepBid  */

export function useDeepBidPropsVisible(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  appEvents: Ref<AppEvents>;
}) {
  const { adgroupData, campaignData, appEvents } = props;
  const { isInstallWithInAppEvent, curDeepExternalActionItem } = useAdgroupAppEvents({
    adgroupFormData: adgroupData,
    appEvents,
    campaignFormData: campaignData,
  });

  const isMultiBidSupport = computed(() => Boolean(curDeepExternalActionItem.value?.is_multi_bid_enabled));
  const isMultiBid = computed(() => adgroupData.value?.deepBidType === DeepBidType.MULTI_BID);

  return {
    visible: computed(() => isInstallWithInAppEvent.value && (isMultiBid.value || isMultiBidSupport.value)),
  };
}

/**  DeepBidCheckbox  */

export function useDeepBidCheckboxPropsDisabled(props: { adgroupConfig: Ref<AdgroupConfig> }) {
  const { adgroupConfig } = props;
  return {
    disabled: computed(() => adgroupConfig.value?.disableBidMode),
  };
}

export function useDeepBidCheckboxPropsTitle(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  appEvents: Ref<AppEvents>;
}) {
  const secondaryGoal = useCommonSecondaryGoal(props);
  const { isOnlyPangleRetention } = useAdgroupAppEvents({
    adgroupFormData: props.adgroupData,
    campaignFormData: props.campaignData,
    appEvents: props.appEvents,
  });
  return {
    title: computed(() =>
      (isOnlyPangleRetention.value ?
        i18n.t('module_bidding_retention_goal') :
        i18n.t('module_bidding_cap_the_cost_of_deep_bid', {
          sencondaryGoal: secondaryGoal.value,
        }))
    ),
  };
}

export function useDeepBidCheckboxPropsTips(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  appEvents: Ref<AppEvents>;
}) {
  const secondaryGoal = useCommonSecondaryGoal(props);
  const { isOnlyPangleRetention } = useAdgroupAppEvents({
    adgroupFormData: props.adgroupData,
    campaignFormData: props.campaignData,
    appEvents: props.appEvents,
  });
  return {
    tips: computed(() =>
      (isOnlyPangleRetention.value ?
        i18n.t('module_bidding_retention_goal_tip') :
        i18n.t('module_bidding_cap_the_cost_of_deep_bid_tip', {
          sencondaryGoal: secondaryGoal.value,
        }))
    ),
  };
}

/**  DeepBidSet  */

export function useDeepBidSetPropsInputType(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  appEvents: Ref<AppEvents>;
}) {
  const { isOnlyPangleRetention } = useAdgroupAppEvents({
    adgroupFormData: props.adgroupData,
    campaignFormData: props.campaignData,
    appEvents: props.appEvents,
  });

  return {
    inputType: computed(() => (isOnlyPangleRetention.value ? 'percent' : 'number')),
  };
}

export function useDeepBidSetPropsCurrencyUnit(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  appEvents: Ref<AppEvents>;
}) {
  const deepCurrencyUnit = useCommonDeepCurrencyUnit({
    adgroupData: props.adgroupData,
    campaignData: props.campaignData,
    appEvents: props.appEvents,
  });

  return {
    currencyUnit: deepCurrencyUnit,
  };
}

export function useDeepBidSetPropsDatakey(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  appEvents: Ref<AppEvents>;
}) {
  const { isOnlyPangleRetention } = useAdgroupAppEvents({
    adgroupFormData: props.adgroupData,
    campaignFormData: props.campaignData,
    appEvents: props.appEvents,
  });

  return {
    dataKey: computed(() => (isOnlyPangleRetention.value ? 'dailyRetentionRatio' : 'deepCpabid')),
  };
}

/**  DeepBidSecondRateReminder  */

export function useDeepBidSecondRateReminderPropsTitle(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  appEvents: Ref<AppEvents>;
}) {
  const { adgroupData } = props;
  const secondaryGoal = useCommonSecondaryGoal(props);
  const { isOnlyPangleRetention } = useAdgroupAppEvents({
    adgroupFormData: props.adgroupData,
    campaignFormData: props.campaignData,
    appEvents: props.appEvents,
  });
  const deepCurrencyUnit = useCommonDeepCurrencyUnit({
    adgroupData: props.adgroupData,
    campaignData: props.campaignData,
    appEvents: props.appEvents,
  });
  return {
    title: computed(() => {
      if (isOnlyPangleRetention.value) {
        if (!adgroupData.value?.deepCpabid || !adgroupData.value?.cpaBid) {
          return i18n.t('adgroup_deep_secondary_only_pangle_retention_new', {
            deepEvent: secondaryGoal.value,
          });
        }

        return i18n.t('adgroup_deep_secondary_only_pangle_retention_rate_new', {
          bid: adgroupData.value?.deepCpabid,
          unit: deepCurrencyUnit.value,
          rate: Number(adgroupData.value?.dailyRetentionRatio).toFixed(2),
          deepEvent: secondaryGoal.value,
        });
      }

      let rate = adgroupData.value?.dailyRetentionRatio ? adgroupData.value?.dailyRetentionRatio.toFixed(2) : 0;
      if (adgroupData.value?.cpaBid && adgroupData.value?.deepCpabid) {
        rate = rate || ((adgroupData.value?.cpaBid * 100) / adgroupData.value?.deepCpabid).toFixed(2);
      }
      switch (adgroupData.value?.deepExternalAction) {
        case EMEventType.PURCHASE: {
          return rate ?
            i18n.t('adgroup_deep_secondary_payment_rate_num', { rate }) :
            i18n.t('adgroup_deep_secondary_payment_rate');
        }
        case EMEventType.NEXT_DAY_OPEN: {
          return rate ?
            i18n.t('adgroup_deep_secondary_retention_rate_num', { rate }) :
            i18n.t('adgroup_deep_secondary_retention_rate');
        }
        default:
          return '';
      }
    }),
  };
}

export function useDeepBidSecondRateReminderPropsTips(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  appEvents: Ref<AppEvents>;
}) {
  const { isOnlyPangleRetention } = useAdgroupAppEvents({
    adgroupFormData: props.adgroupData,
    campaignFormData: props.campaignData,
    appEvents: props.appEvents,
  });
  return {
    tips: computed(() => {
      if (isOnlyPangleRetention.value) {
        return i18n.t('adgroup_deep_secondary_retention_only_pange_tip');
      }

      switch (props?.adgroupData?.value?.deepExternalAction) {
        case EMEventType.PURCHASE: {
          return i18n.t('adgroup_deep_secondary_payment_rate_tip');
        }
        case EMEventType.NEXT_DAY_OPEN: {
          return i18n.t('adgroup_deep_secondary_retention_rate_tip');
        }
        default:
          return '';
      }
    }),
  };
}
